import { FormField, Select, useToast, type MenuItem } from '@ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage, formErrors } from '../../../lib/errors';
import { useChangeRole, useRemoveMember, useRoles, type Member, type Role } from '../api';
import { useMeta } from '../../system/useMeta';
import { grantable } from '../permissions';
import { useSession } from '../session';

/** Roles the signed-in member may hand out: nothing beyond their own permissions. */
export function useGrantableRoles(): Role[] {
  const { can } = useSession();
  const roles = useRoles();
  return (roles.data ?? []).filter((r) => grantable(r.permissions, can));
}

/**
 * "Rolle ändern …" and "Entfernen" (or "Imkerei verlassen" for oneself) for
 * one member, as items for a "…" menu plus their dialogs (#36). Used in the
 * member list and on the member's detail page.
 */
export function useMemberActions(
  member: Member,
  {
    soleOwner,
    onRemoved,
  }: {
    /** The member is the only owner and so cannot leave (business.last_owner). */
    soleOwner: boolean;
    /** After someone else was removed, e.g. back to the list. */
    onRemoved?: () => void;
  },
): { items: MenuItem[]; dialogs: ReactNode } {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { me, can } = useSession();
  const meta = useMeta();
  const roles = useGrantableRoles();
  const remove = useRemoveMember();
  const [dialog, setDialog] = useState<'role' | 'remove' | null>(null);
  const isSelf = member.user_id === me.id;
  // Only roles one may grant, and only for members whose role one may grant (USR-005).
  const canManage = can('members.manage') && roles.some((r) => r.id === member.role.id);
  const canChangeRole = canManage && !isSelf;
  const canRemove = (canManage || isSelf) && !(isSelf && soleOwner);
  const removeLabel = isSelf ? t('members.leave') : t('members.remove');

  const items: MenuItem[] = [
    ...(canChangeRole
      ? [{ key: 'role', label: t('members.changeRole'), onSelect: () => setDialog('role') }]
      : []),
    ...(canRemove
      ? [
          {
            key: 'remove',
            label: removeLabel,
            tone: 'danger' as const,
            onSelect: () => setDialog('remove'),
          },
        ]
      : []),
  ];

  const dialogs = (
    <>
      {canChangeRole && (
        <ChangeRoleDialog
          open={dialog === 'role'}
          member={member}
          roles={roles}
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDialog
        open={dialog === 'remove'}
        onClose={() => {
          remove.reset();
          setDialog(null);
        }}
        title={isSelf ? t('members.leaveTitle') : t('members.removeTitle', { name: member.name })}
        description={
          isSelf
            ? t('members.leaveText', { name: meta.data?.business_name ?? '' })
            : t('members.removeText', { name: member.name })
        }
        confirmLabel={removeLabel}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          remove.mutate(member.user_id, {
            onSuccess: () => {
              setDialog(null);
              if (isSelf) {
                // The account is gone, and with it the session.
                navigate('/anmelden', { replace: true });
              } else {
                toast.show(t('members.removed', { name: member.name }));
                onRemoved?.();
              }
            },
          })
        }
      />
    </>
  );

  return { items, dialogs };
}

function ChangeRoleDialog({
  open,
  member,
  roles,
  onClose,
}: {
  open: boolean;
  member: Member;
  roles: Role[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const changeRole = useChangeRole();
  const [chosen, setChosen] = useState<string | null>(null);
  const roleId = chosen ?? member.role.id;

  const close = () => {
    changeRole.reset();
    setChosen(null);
    onClose();
  };
  const onSubmit = () => {
    if (roleId === member.role.id) return close();
    changeRole.mutate(
      { userId: member.user_id, roleId },
      {
        onSuccess: () => {
          toast.show(t('members.roleChanged'));
          close();
        },
      },
    );
  };
  const errors = formErrors(changeRole.error, { role: ['role.'] });

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={t('members.changeRoleTitle', { name: member.name })}
      submitLabel={t('common.save')}
      onSubmit={onSubmit}
      pending={changeRole.isPending}
      error={errors.general}
      size="sm"
    >
      <FormField label={t('members.roleLabel')} error={errors.field.role} required>
        {(p) => (
          <Select
            {...p}
            value={roleId}
            onChange={(e) => setChosen(e.target.value)}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
